import { useCallback, useEffect, useState } from "react";
import { AxiosError } from "axios";
import { apiV2 } from "api/firebase";
import { IEmbeddedShow } from "@evennt-tech/api-v2-core";
import { AnonymousOldShow } from "ts/api/show";
import { getErrorMessage } from "tools/error";

type EmbeddedShowsState<T extends AnonymousOldShow> = Pick<
  IEmbeddedShow,
  "buttonColor" | "buttonTextColor"
> & { shows: T[] };

// What api-v2 answers when the list, or the show on it, has been switched off —
// as opposed to a request that simply failed.
const UNAVAILABLE_STATUSES = [403, 404];

/**
 * Loads the organiser's embedded show list (or one show, when `showId` is
 * given) straight from api-v2 with the organiser's embed token. The API
 * answers 403 when the token is invalid or the list is disabled and 404 when
 * the show is not part of the list.
 */
const requestEmbeddedShows = async <T extends AnonymousOldShow>(
  token: string,
  showId?: string | null
): Promise<EmbeddedShowsState<T>> => {
  const response = await apiV2.embeddedShows.getShows({
    token,
    showId: showId ?? undefined,
  });
  const embeddedShows = response?.embeddedShows;

  if (!embeddedShows?.length) {
    return { shows: [] };
  }

  const { buttonColor, buttonTextColor } = response;

  return {
    // The widget's public projection of a show; `T` narrows it per page.
    shows: (embeddedShows as unknown) as T[],
    buttonColor,
    buttonTextColor,
  };
};

const fetchEmbeddedShows = <T extends AnonymousOldShow = AnonymousOldShow>(
  token?: string,
  showId?: string | null
): [EmbeddedShowsState<T>, boolean, () => Promise<boolean>] => {
  const [embeddedShowsState, setEmbeddedShowsState] = useState<
    EmbeddedShowsState<T>
  >({ shows: [] });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  useEffect(() => {
    if (!token) {
      setEmbeddedShowsState({ shows: [] });
      setIsLoading(false);
      return;
    }
    // A response for a token / showId the page has since moved away from must
    // not overwrite the current one.
    let isCurrent = true;
    const getShows = async () => {
      try {
        setIsLoading(true);
        const state = await requestEmbeddedShows<T>(token, showId);
        if (!isCurrent) return;
        setEmbeddedShowsState(state);
        setIsLoading(false);
      } catch (e) {
        if (!isCurrent) return;
        setEmbeddedShowsState({ shows: [] });
        setIsLoading(false);
        console.error(
          `Error occurred fetching embedded show list: ${getErrorMessage(e)}`
        );
      }
    };
    getShows();
    return () => {
      isCurrent = false;
    };
  }, [token, showId]);

  const refresh = useCallback(async (): Promise<boolean> => {
    if (!token) {
      return false;
    }

    try {
      const state = await requestEmbeddedShows<T>(token, showId);

      if (!state.shows.length) {
        setEmbeddedShowsState({ shows: [] });
      }

      return state.shows.length > 0;
    } catch (e) {
      const status = (e as AxiosError | undefined)?.response?.status;
      if (status && UNAVAILABLE_STATUSES.includes(status)) {
        setEmbeddedShowsState({ shows: [] });
        return false;
      }

      throw e;
    }
  }, [token, showId]);

  return [embeddedShowsState, isLoading, refresh];
};
export default fetchEmbeddedShows;
